import { computed, ref } from 'vue'
import { createGlobalState } from '@vueuse/core'
import { NAVIGATION_COMPANY_HIERARCHY_API_URL } from '@/vue/helpers/apiURLS'
import { fetchApiData } from '@/vue/helpers/getApiData'
import type { BreadcrumbAsset, BreadcrumbPortfolio } from './breadcrumb.types'
import type { NavigationCompanyHierarchyResponse } from '@/vue/types/navigation'
import { useCompanyList } from './use-company-list'

export const useCompanyHierarchy = createGlobalState(() => {
    const { companyMap } = useCompanyList()

    const resolvedCompanyId = ref<number | null>(null)
    const portfolioMap = ref<Map<number, BreadcrumbPortfolio>>(new Map())
    const assetMap = ref<Map<number, BreadcrumbAsset>>(new Map())
    const isLoading = ref(false)
    const error = ref<string | null>(null)

    async function fetchCompanyHierarchy(params: {
        byCompanyId?: number
        byPortfolioId?: number
        byAssetId?: number
    }) {
        const { byCompanyId, byPortfolioId, byAssetId } = params

        const queryParams: Record<string, number> = {}
        if (byCompanyId !== undefined) {
            queryParams.by_company_id = byCompanyId
        } else if (byPortfolioId !== undefined) {
            queryParams.by_portfolio_id = byPortfolioId
        } else if (byAssetId !== undefined) {
            queryParams.by_asset_id = byAssetId
        } else {
            console.error('fetchCompanyHierarchy: No ID provided')
            return { company_id: 0, portfolios: [] }
        }

        isLoading.value = true
        error.value = null

        try {
            const { data, error: apiError } = await fetchApiData<NavigationCompanyHierarchyResponse>(
                NAVIGATION_COMPANY_HIERARCHY_API_URL,
                queryParams,
            )

            if (apiError || !data) {
                error.value = apiError ?? 'Failed to fetch company hierarchy'
                console.error('Failed to fetch company hierarchy:', apiError)
                return { company_id: 0, portfolios: [] }
            }

            const hierarchyData = data.data
            const companyIdFromApi = hierarchyData.company_id

            resolvedCompanyId.value = companyIdFromApi
            rebuildMaps(companyIdFromApi, hierarchyData)

            return hierarchyData
        } catch (err) {
            error.value = err instanceof Error ? err.message : 'Failed to fetch company hierarchy'
            console.error('Error fetching company hierarchy:', err)
            return { company_id: 0, portfolios: [] }
        } finally {
            isLoading.value = false
        }
    }

    function rebuildMaps(companyId: number, hierarchyData: { company_id: number; portfolios: NavigationCompanyHierarchyResponse['data']['portfolios'] }) {
        const breadcrumbCompany = companyMap.value.get(companyId)

        if (!breadcrumbCompany) {
            error.value = 'Company not found in company list'
            console.error(`rebuildMaps: company ${companyId} not found in companyMap`)
            return
        }

        portfolioMap.value.clear()
        assetMap.value.clear()
        breadcrumbCompany.portfolios = []

        const sortedPortfolios = [...hierarchyData.portfolios].sort((a, b) => a.name.localeCompare(b.name))

        sortedPortfolios.forEach((apiPortfolio) => {
            const breadcrumbPortfolio: BreadcrumbPortfolio = {
                id: apiPortfolio.id,
                name: apiPortfolio.name,
                type: 'portfolio',
                company: breadcrumbCompany,
                default_reporting_year: apiPortfolio.default_reporting_year,
                oldest_reporting_year: apiPortfolio.oldest_reporting_year,
                last_reporting_year: apiPortfolio.last_reporting_year,
                assets: [],
                asset_only_access: apiPortfolio.asset_only_access,
                asset_only_write_access: apiPortfolio.asset_only_write_access,
            }

            breadcrumbCompany.portfolios.push(breadcrumbPortfolio)
            portfolioMap.value.set(apiPortfolio.id, breadcrumbPortfolio)

            const sortedAssets = [...apiPortfolio.assets].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))

            sortedAssets.forEach((apiAsset) => {
                const breadcrumbAsset: BreadcrumbAsset = {
                    id: apiAsset.id,
                    name: apiAsset.name,
                    portfolio: breadcrumbPortfolio,
                    company: breadcrumbCompany,
                    type: 'asset',
                    client_id: apiAsset.client_id,
                    owned_since_year: apiAsset.owned_since_year,
                    owned_until_year: apiAsset.owned_until_year,
                    active: apiAsset.active ?? true,
                }

                breadcrumbPortfolio.assets.push(breadcrumbAsset)
                assetMap.value.set(apiAsset.id, breadcrumbAsset)
            })
        })
    }

    async function setSelectedCompany(companyId: number) {
        resolvedCompanyId.value = companyId
        await fetchCompanyHierarchy({ byCompanyId: companyId })
    }

    function getPortfolioById(id: number): BreadcrumbPortfolio | null {
        return portfolioMap.value.get(id) || null
    }

    function getAssetById(id: number): BreadcrumbAsset | null {
        return assetMap.value.get(id) || null
    }

    const portfolios = computed(() => Array.from(portfolioMap.value.values()))
    const assets = computed(() => Array.from(assetMap.value.values()))

    return {
        resolvedCompanyId,
        portfolios,
        assets,
        fetchCompanyHierarchy,
        setSelectedCompany,
        getPortfolioById,
        getAssetById,
        isLoading,
        error,
    }
})
